import { colors, sxPresets } from '@/theme'
import { ArrowForward } from '@mui/icons-material'
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material'
import { formatDate } from '@/lib/utils'
import CollapsibleCard from './CollapsibleCard'
import MedReqStatusBadge from './MedReqStatusBadge'

const SUBSCRIPTION_LABELS: Record<string, string> = {
  shop_subscription: 'Subscription',
  shop_order_refund: 'Refund',
  shop_order: 'Order'
}

function formatSubscriptionLabel(value: string): string {
  return SUBSCRIPTION_LABELS[value] ?? value
}

function formatCardDate(date: string): string {
  try {
    return formatDate(new Date(date))
  } catch {
    return date
  }
}

export interface MedicationRequestItem {
  id: number | string
  name: string
  status: string
  date: string
  orderNumber?: string
  subscription?: string
  type?: string
  prescribedItem?: string
}

interface PreviousMedicationRequestsListProps {
  medicationRequests: MedicationRequestItem[]
  isLoading?: boolean
  sectionId?: string
  title?: string
  onItemClick?: (id: number | string) => void
}

export default function PreviousMedicationRequestsList({
  medicationRequests,
  isLoading = false,
  sectionId = 'medication-requests',
  title = 'Previous Medication Requests',
  onItemClick
}: PreviousMedicationRequestsListProps) {
  const isEmpty = !isLoading && (!medicationRequests || medicationRequests.length === 0)

  return (
    <CollapsibleCard sectionId={sectionId} title={title} isEmpty={isEmpty} emptyMessage='No medication requests found'>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box
          sx={{
            ...sxPresets().section.content,
            bgcolor: colors.background.surface,
            ...sxPresets().grid.threeColumn,
            ...sxPresets().scrollable.twoRows
          }}
        >
          {medicationRequests?.map(med => (
            <Paper
              key={med.id}
              onClick={() => onItemClick?.(med.id)}
              elevation={0}
              sx={{
                ...sxPresets().card.default,
                ...(onItemClick ? sxPresets().card.clickable : {}),
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Status Chip */}
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <MedReqStatusBadge status={med.status} />
              </Box>

              {/* Card Body */}
              <Box sx={{ px: 2, pb: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant='smallText'>{med.type || 'Condition'}</Typography>
                <Typography variant='cardTitle'>{med.name}</Typography>
                {med.prescribedItem && (
                  <Box
                    sx={{
                      mt: 0.5,
                      mb: 0.25,
                      px: 1.25,
                      py: 0.875,
                      borderRadius: 1,
                      bgcolor: colors.brand.primaryLight,
                      borderLeft: `3px solid ${colors.brand.primary}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: colors.brand.primary,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        lineHeight: 1
                      }}
                    >
                      Prescribed Item
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: colors.text.dark,
                        lineHeight: 1.35,
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {med.prescribedItem}
                    </Typography>
                  </Box>
                )}
                {med.orderNumber && <Typography variant='smallText'>Order #{med.orderNumber}</Typography>}
                {med.date && <Typography variant='smallText'>{formatCardDate(med.date)}</Typography>}
                {med.subscription && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={formatSubscriptionLabel(med.subscription)} size='small' variant='neutral' />
                  </Box>
                )}
              </Box>

              {/* Card Footer */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                  py: 1.5,
                  bgcolor: colors.brand.primaryLight
                }}
              >
                <Typography variant='link' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  View request
                  <ArrowForward sx={{ fontSize: '14px' }} />
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </CollapsibleCard>
  )
}
